import React, { useEffect } from 'react';
import { PayrollProviderName } from 'api/EmployersApi';
import { getApplicationData } from 'selectors/getApplicationData';
import { useSelector } from 'react-redux';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { FlowComponentType } from 'routes/types';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { updateApplicationStatus } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { usePartnerData } from 'enums/PartnerName';
import { AtomicProductSupport, GetApplicationData } from 'handlers/applicationData';
import FormNavigation from 'components/FormNavigation';

import ManualDeduction from './ManualDeduction';
import AutomaticDeduction from './AutomaticDeduction';
import { hasAtomicProductSupport } from './AutomaticDeduction/payrollProvider/atomic/utils';

export const validateAutomaticDeduction = (application?: GetApplicationData) => {
  const payrollProvider = application?.employment.employer1?.payrollProvider ?? PayrollProviderName.Manual;
  const isPayrollProviderAtomic = payrollProvider === PayrollProviderName.Atomic;
  const hasAtomicDepositSupport =
    isPayrollProviderAtomic && hasAtomicProductSupport(AtomicProductSupport.Deposit, application);

  const shouldShowAutomaticDeduction = [
    PayrollProviderName.Argyle,
    PayrollProviderName.Pinwheel,
    ...(hasAtomicDepositSupport ? [PayrollProviderName.Atomic] : []),
  ].includes(payrollProvider);

  return { shouldShowAutomaticDeduction, payrollProvider };
};

const SetupDeduction = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const navigate = useNavigate();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const { application } = useSelector(getApplicationData);
  const { directDepositInstructions } = usePartnerData();

  const { payrollProvider, shouldShowAutomaticDeduction } = validateAutomaticDeduction(application);

  useEffect(() => {
    if (payrollProvider === PayrollProviderName.ManuallySendToHR) {
      (async () => {
        if (application?.status === ApplicationStatusName.SetUpDeduction) {
          await dispatchWithUnwrap(
            updateApplicationStatus({
              applicationId: application!.id,
              applicationStatus: ApplicationStatusName.FinalReview,
            }),
          );
        }
        navigate(RoutePath.OfferStatus);
      })();
    } else if (payrollProvider === PayrollProviderName.Manual && directDepositInstructions) {
      navigate(RoutePath.SetupViaPayroll);
    }
  }, []);

  return (
    <>
      {shouldShowAutomaticDeduction ? (
        <AutomaticDeduction navigationInfo={{ ...navigationInfo }} handleNext={handleNext} />
      ) : (
        <>
          <FormNavigation {...navigationInfo} />
          <ManualDeduction />
        </>
      )}
    </>
  );
};

export default SetupDeduction;
